/**
 * AUTH CONTEXT - Autenticación Segura con httpOnly Cookies
 * =========================================================
 *
 * Este contexto maneja la autenticación de forma segura:
 * - NO almacena tokens en localStorage (vulnerable a XSS)
 * - Usa httpOnly cookies (manejadas por el servidor)
 * - Verifica la sesión llamando a /api/auth/me
 * - Solo almacena datos NO sensibles del usuario en memoria
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Crear contexto
const AuthContext = createContext(null);

// Axios configurado para cookies
const authAxios = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Enviar/recibir cookies httpOnly
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Provider de autenticación
 */
export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Verificar sesión actual llamando al servidor
   * El servidor valida la cookie httpOnly
   */
  const verificarSesion = useCallback(async () => {
    try {
      const response = await authAxios.get('/auth/me');
      if (response.data.success && response.data.data) {
        setUsuario(response.data.data);
        setError(null);
        return true;
      }
      setUsuario(null);
      return false;
    } catch (err) {
      // 401 significa que no hay sesión válida (normal)
      if (err.response?.status !== 401) {
        console.error('Error verificando sesión:', err);
      }
      setUsuario(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Iniciar sesión
   */
  const login = async (email, password) => {
    setError(null);
    // No usar setLoading aquí - PublicRoute muestra LoadingScreen cuando loading=true,
    // lo que desmonta el componente Login y pierde el estado de error.
    // Login.jsx tiene su propio estado de loading.

    try {
      const response = await authAxios.post('/auth/login', { email, password });

      if (response.data.success && response.data.data?.usuario) {
        // El token se establece automáticamente como cookie httpOnly
        const userData = response.data.data.usuario;
        setUsuario(userData);
        return { success: true, usuario: userData };
      }

      throw new Error('Respuesta inválida del servidor');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al iniciar sesión';
      const errorCode = err.response?.data?.code || null;
      setError(errorMsg);
      return { success: false, error: errorMsg, code: errorCode };
    }
  };

  /**
   * Cerrar sesión
   */
  const logout = async () => {
    try {
      await authAxios.post('/auth/logout');
    } catch (err) {
      console.error('Error en logout:', err);
    } finally {
      // Limpiar estado en memoria
      setUsuario(null);
      setError(null);
    }
  };

  /**
   * Verificar si el usuario es admin
   */
  const esAdmin = () => {
    return usuario?.rol === 'admin';
  };

  /**
   * Verificar si está autenticado
   */
  const estaAutenticado = () => {
    return !!usuario;
  };

  // Verificar sesión al montar el componente
  useEffect(() => {
    verificarSesion();
  }, [verificarSesion]);

  // Valor del contexto
  const value = {
    usuario,
    loading,
    error,
    login,
    logout,
    verificarSesion,
    esAdmin,
    estaAutenticado,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook para usar el contexto de autenticación
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}

export default AuthContext;
